import { MapPin, MapPinOff } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface AmenityPinsToggleProps {
  active: boolean;
  onToggle: () => void;
}

/**
 * `<AmenityPinsToggle>` — single chip controlling whether amenity marker
 * pins render on `/app/map`. Default OFF per user intent ("I should never
 * see a pin"). The in/out-of-radius classification still runs regardless,
 * so HighlightToggle and RadiusOnlyToggle work either way — this only
 * controls whether the pin glyphs are visible.
 *
 * Active = MapPin (filled). Inactive = MapPinOff (muted). Coral-500 accent
 * keys "annotation/marker" — distinct from the moss/bronze/tide chips next
 * to it.
 *
 * Accessibility: `role="switch"` + `aria-checked`; aria-label is the long
 * form for screen readers.
 */
export function AmenityPinsToggle({ active, onToggle }: AmenityPinsToggleProps) {
  const Icon = active ? MapPin : MapPinOff;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={active}
      aria-label="Show amenity marker pins on the map"
      onClick={onToggle}
      className={cn(
        'inline-flex h-8 px-3 rounded-full border items-center gap-1.5 text-sm font-semibold cursor-pointer',
        'transition-colors',
        'focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2',
        active
          ? 'bg-coral-500/20 border-coral-500/60 text-foam-50'
          : 'bg-ocean-800/40 border-foam-50/10 text-foam-50/60',
      )}
    >
      <Icon className="w-4 h-4 shrink-0" aria-hidden />
      Show pins
    </button>
  );
}
